import { Card, Image, Group, Text, ActionIcon, Stack, Badge, Box, Tooltip } from '@mantine/core';
import { IconScale, IconScaleOff, IconHeart, IconDots } from '@tabler/icons-react';
import { RarityBadge } from '@/components/RarityBadge';
import { Popularity } from '@/components/Popularity';
import { useStore } from '@/store';
import { formatKNumber, translate } from '@/lib';
import { getRareInfo, getGenreName } from '@/models/Metadata';
import type { ApiItem, ApiItemDetail } from '@/models/Item';
import style from './Style.module.scss';
import clsx from 'clsx';

interface ItemCardProps {
  item: ApiItem | ApiItemDetail;
  rank?: number;
  onClick?: (item: ApiItem | ApiItemDetail) => void;
  isDetail?: boolean;
  className?: string;
}

export function ItemCard({ item, rank, onClick, isDetail, className }: ItemCardProps) {
  const { compareList, addToCompare, removeFromCompare, isInCompareList } = useStore();

  // Get metadata
  const rareInfo = getRareInfo(item.rare);
  const genreName = getGenreName(item.genre);

  // Get tier background class name
  const tierBgClass = item.rare ? `tier-bg-${item.rare}` : 'tier-bg-1';
  // const categoryName = getCategoryName(item.category);

  // Category checks using API category IDs
  const isGun = item?.stats;
  // const isOutfit = item.category === 1;
  // const isCollection = item.category >= 3; // character, emote, etc.

  const inCompareList = isInCompareList(item?.id);

  const compareListFull = compareList.length >= 3;

  const handleCompareClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (inCompareList) {
      removeFromCompare(item?.id);
    } else if (!compareListFull) {
      addToCompare(item as any); // Store expects Item type
    }
  };

  const handleCardClick = () => {
    onClick?.(item);
  };

  // Show stats for guns
  const showStats = isGun && item?.stats;

  // Build array of stats that have non-zero values
  const statEntries = showStats ? [
    item.stats?.damage && { value: item.stats.damage, label: translate('ff.item.stats.short.damage', 'DMG') },
    item.stats?.accuracy && { value: item.stats.accuracy, label: translate('ff.item.stats.short.accuracy', 'ACC') },
    item.stats?.range && { value: item.stats.range, label: translate('ff.item.stats.short.range', 'RNG') },
    item.stats?.speed && { value: item.stats.speed, label: translate('ff.item.stats.short.speed', 'ROF') },
    item.stats?.reload && { value: item.stats.reload, label: translate('ff.item.stats.short.reload', 'RLD') },
    item.stats?.cartridge && { value: item.stats.cartridge, label: translate('ff.item.stats.short.cartridge', 'MAG') },
    item.stats?.biteArmor && { value: item.stats.biteArmor, label: translate('ff.item.stats.short.biteArmor', 'PEN') },
    item.stats?.playerSpeedFactor && { value: item.stats.playerSpeedFactor, label: translate('ff.item.stats.short.playerSpeedFactor', 'MOV') },
  ].filter(Boolean) as { value: number; label: string }[] : [];

  const hasOverflowStats = statEntries.length > 3;

  return (
    <Card
      className={clsx(style.itemCard, className, {
        [style.isDetail]: isDetail,
      })}
      padding="xs"
      radius="md"
      onClick={handleCardClick}
      style={{ cursor: onClick ? 'pointer' : 'default' }}
    >
      <div className={style.itemCardContent}>
        <Group wrap="nowrap" align="flex-start" className={clsx(style.groupItemContent, {
          [style['groupItemContent--has-evo']]: !!item.isOwned,
        })}>
          {/* Rank Badge */}
          {rank && (
            <div className={style.rankBadge}>
              #{rank}
            </div>
          )}

          {/* Small Square Image */}
          <div className={`${style.imageContainer} ${tierBgClass}`}>
            <Image
              src={item.icon}
              alt={item.name}
              width={80}
              height={80}
              fit="cover"
              radius="sm"
            />

            {!!item.stats?.evoLevel && (
              <Badge className={style.evoBadge} size="xs" color="green">
                {translate('ff.item.stats.evoLevel', 'Evo')}: {item.stats?.evoLevel}
              </Badge>
            )}
          </div>

          {/* Content */}
          <Box style={{ flex: 1, minWidth: 0 }}>
            <Stack gap="0">
              <div>
                <Text fw={600} size="md" lineClamp={1}>
                  {item.translate || item.name}
                </Text>
                <Group gap="xs" mt={4}>
                  <RarityBadge rarity={rareInfo.name as any} rare={item.rare} size="xs" />
                  {/* Show genre tag */}
                  {genreName && (
                    <Badge
                      size="xs"
                      variant="dot"
                      color="blue"
                    >
                      {genreName}
                    </Badge>
                  )}
                </Group>
              </div>

              {item.isOwned && (
                <div>
                  <Badge className={style.ownedBadge} size="xs" variant="filled" color="teal" radius="sm">
                    {translate('ff.item.owned', 'Owned')}
                  </Badge>
                </div>
              )}

              {/* Show stats for guns */}
              {statEntries.length > 0 && (
                <Group className={style.statsGroup} gap="xs" wrap="nowrap">
                  {[...statEntries]?.sort((a, b) => b.value - a.value).map((stat, index) => (
                    <Text
                      key={index}
                      className={style.statItem}
                      size="xs"
                      style={{ textAlign: 'center' }}
                      c={stat.value > 0 ? 'green' : stat.value < 0 ? 'red' : undefined}
                      fw={600}
                    >
                      {stat.value > 0 && '+'}{stat.value}
                      <br />
                      <span style={{ display: 'block', whiteSpace: 'nowrap' }}>{stat.label}</span>
                    </Text>
                  ))}
                  {hasOverflowStats && (
                    <span className={style.statsDots}>
                      <IconDots size={16} />
                    </span>
                  )}
                </Group>
              )}

              {/* Show category for collection items */}
              {/* {isCollection && categoryName && (
                <Text size="xs" c="dimmed" tt="lowercase">
                  {categoryName}
                </Text>
              )} */}
            </Stack>
          </Box>

          {/* Actions */}
          <Group gap="xs" wrap="nowrap" className={style.itemActions}>
            {isGun && (
              <Tooltip
                label={inCompareList ? translate('ff.action.removeFromCompare', 'Remove from compare') : compareListFull ? translate('ff.action.compareListFull', 'Compare list full (max 3)') : translate('ff.action.addToCompare', 'Add to compare')}
              >
                <ActionIcon
                  variant={inCompareList ? 'filled' : 'subtle'}
                  color={inCompareList ? 'orange' : 'gray'}
                  onClick={handleCompareClick}
                  disabled={!inCompareList && compareListFull}
                >
                  {inCompareList ? <IconScaleOff size={18} /> : <IconScale size={18} />}
                </ActionIcon>
              </Tooltip>
            )}
            {/* Wishlist count display */}
            <Group gap={4} wrap="nowrap">
              <IconHeart size={16} color="var(--mantine-color-red-5)" />
              <Text size="xs" c="dimmed" fw={500}>
                {formatKNumber(item.wishlist)}
              </Text>
            </Group>
          </Group>

          {/* {onClick && (
            <ActionIcon variant="subtle" color="gray">
              <IconChevronRight size={18} />
            </ActionIcon>
          )} */}
        </Group>

        {/* Popularity at bottom */}
        <Box mt="sm">
          <Popularity
            itemId={item?.id}
            likes={item.like}
            dislikes={item.dislike}
            reaction={item.reaction}
            variant="simplified"
          />
        </Box>
      </div>
    </Card>
  );
}
